// Shared helpers for the two-level manager hierarchy (L1/L2) used by the
// Consolidated Report and Export Reports "Member / Manager" dropdowns.
//
// Member ids and manager ids live in separate id spaces and can collide
// numerically, so a manager selection is namespaced with this prefix to keep it
// distinct from numeric member ids (and the empty-string "All" option).
export const MANAGER_OPTION_PREFIX = 'MGR-';

/**
 * Derives the distinct client list from a subtree manager list (self + L1
 * children). One entry per clientId, first record wins.
 */
export const deriveSubtreeClients = (subtreeManagers) =>
  Object.values(
    (Array.isArray(subtreeManagers) ? subtreeManagers : []).reduce((acc, m) => {
      if (m?.clientId != null && !acc[m.clientId])
        acc[m.clientId] = { clientId: m.clientId, clientName: m.clientName, clientCode: m.clientCode };
      return acc;
    }, {})
  );

/**
 * Derives the L1 manager options for the Managers optgroup from a subtree list.
 * The L2's own record(s) are excluded; their "(Me)" option covers their own data.
 * selfManagerIds accepts a single manager id or a list — an L2 can hold one record per
 * client (all must be excluded, else a second self record shows up as a bogus L1).
 * When selectedClient is not allClientsValue, only L1s for that client are returned.
 */
export const deriveSubtreeL1Options = (subtreeManagers, selfManagerIds, selectedClient, allClientsValue) => {
  const selfIds = new Set(
    (Array.isArray(selfManagerIds) ? selfManagerIds : [selfManagerIds])
      .filter((id) => id != null)
      .map(String)
  );
  return (Array.isArray(subtreeManagers) ? subtreeManagers : [])
    .filter((m) => !selfIds.has(String(m.id)))
    .filter((m) => selectedClient === allClientsValue || String(m.clientId) === String(selectedClient));
};

/**
 * The logged-in manager's full scope: their rolled-up subtree (self + L1 children)
 * unioned with their own client assignments from the session, deduped by manager id
 * (first wins). The subtree alone misses independent assignments (a second _manager
 * record not in the hierarchy); the session list alone misses L1 children. Records
 * without an id are dropped (they can't key a manager-scoped report call).
 */
export const mergeManagerScope = (subtreeManagers, managerClients) => {
  const seen = new Set();
  return [
    ...(Array.isArray(subtreeManagers) ? subtreeManagers : []),
    ...(Array.isArray(managerClients) ? managerClients : []),
  ].filter((m) => {
    if (m?.id == null || seen.has(m.id)) return false;
    seen.add(m.id);
    return true;
  });
};

/**
 * The key identifying the PERSON behind a member or manager record. Two records belong to
 * the same person when they share a userId. A record without one stands only for itself:
 * the DTOs type userId as a primitive, so an unlinked row arrives as 0 rather than null,
 * and keying those on userId would merge every unlinked person into one.
 */
const personKey = (record) => (record?.userId ? `u${record.userId}` : `m${record?.id}`);

/**
 * The same person can be a member or a manager of several clients, producing one record
 * per (person, client). Collapse those to one entry per person.
 */
export const dedupeByPerson = (rows) => {
  const seen = new Set();
  return (Array.isArray(rows) ? rows : []).filter(Boolean).filter((m) => {
    const key = personKey(m);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

// Record ids travel into a query string, so both branches below return numbers. A helper
// whose element type changed on a lookup miss would push that inconsistency onto every caller.
const toRecordId = (value) => Number(value);

/**
 * Every record id held by the person behind `recordId`, drawn from `records`.
 * Mirrors what `resolveOwnerScope` does server-side for the timesheet export.
 *
 * @since NEXT_VERSION
 */
export const siblingRecordIds = (records, recordId) => {
  const list = (Array.isArray(records) ? records : []).filter(Boolean);
  const self = list.find((r) => String(r.id) === String(recordId));
  if (!self) return [toRecordId(recordId)];
  const key = personKey(self);
  return [...new Set(list.filter((r) => personKey(r) === key).map((r) => toRecordId(r.id)))];
};
